import { Link } from "@/lib/nav";
import type { CSSProperties } from "react";

import EmptyState from "@buildpy/ui/empty-state";
import FilterChips from "@/components/filter-chips";
import SectionCard from "@buildpy/ui/section-card";
import Money from "@/components/money";
import StatCard from "@/components/stat-card";
import { Button } from "@buildpy/ui/button";
import ListTable from "@buildpy/ui/list-table";
import {
  TableCell,
  TableRow,
} from "@buildpy/ui/table";
import { EMPTY_MARK, formatElapsed, formatNumber } from "@buildpy/core/lib/format";
import { INQUIRY_WIN_RATE_TITLE } from "@buildpy/core/modules/dashboard/kpi";
import {
  INSIGHT_RANGE_LABEL,
  INSIGHT_RANGES,
  insightRangeHref,
  rangeCaption,
} from "@buildpy/core/modules/insight/range";
import { REPORT_PATH } from "@buildpy/core/modules/insight/monthly-report";
import type { SourceFunnelRow } from "@buildpy/core/modules/insight/source-funnel";
import { funnelContractDiffers } from "@buildpy/core/modules/insight/stage-funnel";
import { DesignerPanel, FunnelPanel, LeadPanel, MonthlyPanel } from "@/modules/insight/ui/stage-panels";
import { formatWinRate } from "@buildpy/core/modules/quote/dashboard";
import type { InsightsView } from "@buildpy/core/views/g1a/insights";
import PageHeader from "@/shell/page-header";

const count = (n: number) => `${formatNumber(n)}건`;

// 수주율 막대 — 바탕은 muted, 채움은 primary 토큰.
const RateBar = ({ rate }: { rate: number | null }) => (
  <div aria-hidden className="h-1.5 w-full rounded-full bg-muted">
    <div className="h-1.5 w-(--w) rounded-full bg-primary" style={{ "--w": `${Math.round((rate ?? 0) * 100)}%` } as CSSProperties} />
  </div>
);

const RateCell = ({ rate }: { rate: number | null }) => (
  <div className="flex min-w-24 flex-col gap-y-1">
    <span className="tabular-nums">{formatWinRate(rate, EMPTY_MARK)}</span>
    <RateBar rate={rate} />
  </div>
);

const SourceRow = ({ row, total = false }: { row: SourceFunnelRow; total?: boolean }) => (
  <TableRow variant={total ? "total" : "default"}>
    <TableCell className="whitespace-nowrap px-3 py-2 font-medium">{row.label}</TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{count(row.inquiries)}</TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{count(row.quoted)}</TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{count(row.won)}</TableCell>
    <TableCell className="px-3 py-2">
      <RateCell rate={row.win_rate} />
    </TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{count(row.contracted)}</TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums"><Money value={row.contract_amount} /></TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums"><Money value={row.avg_amount} /></TableCell>
    <TableCell className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{formatElapsed(row.avg_response_minutes)}</TableCell>
  </TableRow>
);

const SourceCard = ({ row, total = false }: { row: SourceFunnelRow; total?: boolean }) => (
  <SectionCard as="li" className="flex flex-col gap-y-2" data-testid="insight-source-card" padding="sm">
    <div className="flex items-baseline justify-between gap-x-2">
      <span className={total ? "font-semibold" : "font-medium"}>{row.label}</span>
      <span className="text-sm tabular-nums text-muted-foreground">문의 {count(row.inquiries)}</span>
    </div>
    <div className="flex items-center gap-x-2 text-sm">
      <span className="shrink-0 text-muted-foreground">{INQUIRY_WIN_RATE_TITLE}</span>
      <span className="shrink-0 tabular-nums">{formatWinRate(row.win_rate, EMPTY_MARK)}</span>
      <RateBar rate={row.win_rate} />
    </div>
    <dl className="grid grid-cols-3 gap-2 text-sm">
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">견적 발송</dt>
        <dd className="tabular-nums">{count(row.quoted)}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">수주</dt>
        <dd className="tabular-nums">{count(row.won)}</dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">계약</dt>
        <dd className="tabular-nums">{count(row.contracted)}</dd>
      </div>
      <div className="col-span-2 min-w-0">
        <dt className="text-xs text-muted-foreground">계약 금액</dt>
        <dd className="min-w-0"><Money className="break-all whitespace-normal" value={row.contract_amount} /></dd>
      </div>
      <div className="min-w-0">
        <dt className="text-xs text-muted-foreground">건당 계약 금액</dt>
        <dd className="min-w-0"><Money className="break-all whitespace-normal" value={row.avg_amount} /></dd>
      </div>
      <div className="col-span-3 min-w-0">
        <dt className="text-xs text-muted-foreground">평균 첫 응대</dt>
        <dd className="tabular-nums">{formatElapsed(row.avg_response_minutes)}</dd>
      </div>
    </dl>
  </SectionCard>
);

// 유입 분석 — 위는 단계 깔때기·월별·걸린 날·디자이너별(시안), 아래는 유입 경로별 표. 기간(?range=)은 둘이 같이 쓴다.
const Page = ({ data }: { data: InsightsView }) => {
  const { range, loaded } = data;
  const now = new Date(data.now);

  const chips = INSIGHT_RANGES.map((r) => ({ href: insightRangeHref(r), label: INSIGHT_RANGE_LABEL[r] }));
  const rangeChips = (
    <>
      <FilterChips activeHref={insightRangeHref(range)} aria-label="기간" items={chips} />
      <Button asChild size="sm" variant="outline">
        <Link data-testid="insight-report-link" href={REPORT_PATH}>
          월간 리포트
        </Link>
      </Button>
    </>
  );

  if (!loaded) {
    return (
      <>
        <PageHeader actions={rangeChips} title="유입 분석" />
        <EmptyState>
          <span data-testid="insight-failed">유입 분석을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.</span>
        </EmptyState>
      </>
    );
  }

  const { insight, rows, total } = loaded;

  return (
    <>
      <PageHeader actions={rangeChips} description={rangeCaption(range, now, insight.inquiries)} title="유입 분석" />

      {insight.inquiries === 0 ? (
        <EmptyState>
          <span data-testid="insight-empty">이 기간에 들어온 문의가 없습니다. 기간을 넓혀 보세요.</span>
        </EmptyState>
      ) : (
        <>
          <div className="grid gap-4 lg:grid-cols-5" data-testid="insight-stage">
            <div className="flex min-w-0 flex-col gap-4 lg:col-span-3">
              <FunnelPanel contractNote={funnelContractDiffers(insight.funnel, total.contracted)} steps={insight.funnel} />
              <MonthlyPanel months={insight.months} />
            </div>
            <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
              <LeadPanel spans={insight.lead} />
              <DesignerPanel rows={insight.designers} />
            </div>
          </div>

          <section aria-labelledby="insight-sources" className="flex flex-col gap-y-3">
            <div className="flex flex-col gap-y-1">
              <h2 className="text-lg font-semibold" id="insight-sources">
                유입 경로별
              </h2>
              <p className="text-sm text-muted-foreground">어느 경로로 들어온 고객이 계약까지 가는지 봅니다.</p>
            </div>

            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              <StatCard title="문의" value={count(total.inquiries)} />
              <StatCard hint="수주 ÷ 문의 (고객 기준)" title={INQUIRY_WIN_RATE_TITLE} value={formatWinRate(total.win_rate, EMPTY_MARK)} />
              <StatCard
                className="col-span-2 lg:col-span-1"
                hint={`계약 ${count(total.contracted)} · 최초 계약 기준(변경공사 제외)`}
                title="계약 금액"
                value={<Money value={total.contract_amount} />}
              />
              <StatCard
                className="col-span-2 lg:col-span-1"
                hint="간편접수·홈페이지·공사 안내문 문의 기준(직접 등록 제외)"
                title="평균 첫 응대"
                value={formatElapsed(total.avg_response_minutes)}
              />
            </div>

            <ListTable
              className="hidden lg:block"
              columns={[
                { label: "경로" },
                { label: "문의", align: "right" },
                { label: "견적 발송", align: "right" },
                { label: "수주", align: "right" },
                { label: INQUIRY_WIN_RATE_TITLE },
                { label: "계약", align: "right" },
                { label: "계약 금액", align: "right" },
                { label: "건당 계약 금액", align: "right" },
                { label: "평균 첫 응대", align: "right" },
              ]}
              testId="insight-table">
              {rows.map((row) => (
                <SourceRow key={row.label} row={row} />
              ))}
              <SourceRow row={total} total />
            </ListTable>

            <ul className="flex flex-col gap-y-2 lg:hidden" data-testid="insight-cards">
              {rows.map((row) => (
                <SourceCard key={row.label} row={row} />
              ))}
              <SourceCard row={total} total />
            </ul>
          </section>
        </>
      )}

      <p className="text-xs text-muted-foreground">
        가견적은 세지 않습니다. 유입 경로는 고객 정보에서 고칠 수 있습니다.
      </p>
    </>
  );
};

export default Page;
